import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { TableRowV2 } from '@node-space/storybook-components/dist/SimpleTableV2'
import { Text } from '@node-space/storybook-components/dist/Text'
import {
  Avatar,
  Box,
  Box as Container,
  Highlight,
  Icon,
  Box as RowCell,
} from '@node-space/ui-components'
import OnClickProvider from 'components/OnClickProvider/OnClickProvider'
import {
  TablePopoverMenu,
  TablePopoverMenuItemType,
} from 'components/TablePopoverMenu/TablePopoverMenu'
import { PathNames } from 'constants/General'
import { CustomerWallet, CustomerWalletStatusType } from 'types/customers'
import { roundNumberWithCommas } from 'utils/utils'

interface UseCustomerWalletsTableRowDataProps {
  customerWallets: CustomerWallet[]
  isActiveCustomer: boolean
  handleExportTransactionClick: (customerWallet: CustomerWallet) => void
  handleOnDepositClick: (customerWallet: CustomerWallet) => void
  handleOnDetailsClick: (index: number) => void
  handleOpenMoveSheet: (wallet: CustomerWallet) => void
  handleOnPayClick: (wallet: CustomerWallet) => void
}

export const useCustomerWalletsTableRowData = ({
  customerWallets,
  isActiveCustomer,
  handleExportTransactionClick,
  handleOnDepositClick,
  handleOnDetailsClick,
  handleOpenMoveSheet,
  handleOnPayClick,
}: UseCustomerWalletsTableRowDataProps) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [tableRows, setTableRows] = useState<TableRowV2[]>([])

  const handleOnTableRowClick = (customerWallet: CustomerWallet) => {
    navigate(`${PathNames.CUSTOMERS}/${customerWallet?.customerReference}/${customerWallet?.id}`)
  }

  const getActiveCustomerWalletMenuItems = customerWallet => {
    return [
      {
        iconName: 'PaymentIcon',
        label: t('transactionAction.pay'),
        onClick: () => handleOnPayClick(customerWallet),
      },
      {
        iconName: 'ConvertIcon',
        label: t('customers.move'),
        onClick: () => handleOpenMoveSheet(customerWallet),
      },
      {
        iconName: 'DepositIcon',
        label: t('transactionAction.deposit'),
        onClick: () => handleOnDepositClick(customerWallet),
      },
      {
        iconName: 'DownloadIcon',
        label: t('export'),
        onClick: () => handleExportTransactionClick(customerWallet),
      },
    ]
  }

  const getCustomerWalletMenuItems = (
    customerWallet: CustomerWallet,
    customerWalletIndex: number
  ) => {
    return [
      {
        iconName: 'InfoCircleIcon',
        label: t('details'),
        onClick: () => handleOnDetailsClick(customerWalletIndex),
      },
      ...(isActiveCustomer ? getActiveCustomerWalletMenuItems(customerWallet) : []),
    ] as TablePopoverMenuItemType[]
  }

  useEffect(() => {
    const rows: TableRowV2[] = customerWallets?.map((customerWallet, customerWalletIndex) => {
      const isWalletActive = customerWallet?.status === CustomerWalletStatusType.ACTIVE

      return {
        // TODO: Add back once customer wallet transactions are implemented
        // https://coindirectworld.atlassian.net/browse/UCIBAN-72
        // onClick: () => handleOnTableRowClick(customerWallet),
        rowData: [
          {
            cellData: (
              <RowCell paddingL={16} flex centerChildren>
                <Container>
                  <Avatar name={customerWallet?.balance?.currencyCode} />
                </Container>
                <Box paddingY={20} paddingL={16} paddingR={12}>
                  <Text size="sm">{customerWallet?.name}</Text>
                  <Text size="sm" color="grey">
                    {customerWallet?.balance?.currencyCode}
                  </Text>
                </Box>
              </RowCell>
            ),
            testid: 'customer-wallet-name',
          },
          {
            cellData: (
              <RowCell flex paddingR={12}>
                <Text size="sm">{`${roundNumberWithCommas(
                  Number(customerWallet?.balance?.value) || 0,
                  2
                )} ${customerWallet?.balance?.currencyCode}`}</Text>
              </RowCell>
            ),
            testid: `customer-wallet-balance`,
          },
          {
            cellData: !isWalletActive && (
              <RowCell paddingL={24} flex centerChildren>
                <Highlight
                  type="warning"
                  variant="emphasis"
                  iconElement={<Icon name="WarningCircle" size="small" />}
                >
                  {customerWallet?.status}
                </Highlight>
              </RowCell>
            ),
            align: 'center',
            testid: 'customer-wallet-status',
          },
          {
            cellData: isWalletActive && (
              <OnClickProvider id={customerWallet?.id} onClick={() => null}>
                <RowCell flex paddingR={12}>
                  <TablePopoverMenu
                    key={customerWallet?.id}
                    stopPropagation
                    hasPadding
                    testid="apiKeysTablePopverMenu"
                    menuItems={getCustomerWalletMenuItems(customerWallet, customerWalletIndex)}
                  />
                </RowCell>
              </OnClickProvider>
            ),
            align: 'end',
            testid: 'customer-wallet-context-menu',
          },
        ],
      }
    })

    setTableRows(rows)
  }, [customerWallets])

  return tableRows
}
